import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useFetch } from "@/lib/api";
import { PageHeader, Panel, StatusChip, KpiTile, SeverityChip, LoadingState, outcomeLabel, DemoBadge } from "@/components/app/primitives";
import { LiveEngagementPanel } from "@/components/app/LiveSummaryPanel";

const CHANGE_TONE = { milestone: "validated", finding: "ai", escalation: "danger", evidence: "neutral", issue: "risk", action: "crimson" };

export default function CommandCenterPage() {
  const { data: cc, loading } = useFetch("/command-center");
  const navigate = useNavigate();

  if (loading || !cc) return <LoadingState message="Assembling command center..." />;

  return (
    <div data-testid="command-center-page">
      <PageHeader
        kicker="Scene 10 - Executive command center"
        title="Executive Command Center"
        subtitle="The state of the work: baseline confidence, evidence, drivers vs unknowns, open decisions, pilot readiness, and the Six Outcomes scorecard."
        right={<DemoBadge />}
      />

      <LiveEngagementPanel />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-5">
        <KpiTile label="Current OCR" value={`~${cc.baseline.current}%`} sub={cc.baseline.confidence} tone="danger" testId="cc-kpi-current" />
        <KpiTile label="Target" value={`${cc.baseline.target}%`} sub={`Q3 ambition ${cc.baseline.q3}%`} tone="validated" />
        <KpiTile label="Value per point" value="~$13M" sub="EBITDA - finance validation pending" tone="ai" />
        <KpiTile label="Evidence completeness" value={`${cc.evidence_completeness}%`} sub="22 request categories" tone="neutral" />
        <KpiTile label="Data readiness" value={`${cc.data_readiness}%`} sub="Rules-ready; model-gated" tone="ai" />
        <KpiTile label="Open decisions" value={cc.open_decisions.length} sub="All due Day 7-8" tone="risk" testId="cc-kpi-decisions" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4 mb-4">
        <Panel title="Six Outcomes scorecard" testId="cc-outcomes-panel">
          <div className="space-y-2">
            {cc.six_outcomes.map((o) => (
              <div key={o.key} className="flex items-start gap-2.5">
                <StatusChip tone={o.status === "on_track" ? "validated" : o.status === "watch" ? "risk" : "neutral"} className="w-20 justify-center shrink-0 mt-0.5">
                  {o.status === "on_track" ? "On track" : o.status === "watch" ? "Watch" : "Pending"}
                </StatusChip>
                <div>
                  <div className="text-xs font-semibold text-[--tx-1]">{outcomeLabel(o.key)}</div>
                  <div className="text-[11px] text-[--tx-3] leading-snug">{o.note}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="Open decisions - leadership queue" testId="cc-decisions-panel"
          right={<StatusChip tone="risk">{cc.open_decisions.length} pending</StatusChip>}>
          <div className="space-y-2">
            {cc.open_decisions.map((d) => (
              <div key={d.id} className="rounded-lg border border-[--amber] bg-[--amber-soft] p-3" data-testid={`cc-decision-${d.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-mono text-[10px] text-[--amber]">{d.id}</span>
                  <span className="ml-auto font-mono text-[10px] text-[--tx-3]">Due {d.due}</span>
                </div>
                <div className="text-xs font-semibold text-[--tx-1] mb-0.5">{d.title}</div>
                <div className="text-[11px] text-[--tx-3] leading-snug">{d.context}</div>
                <div className="text-[10px] text-[--tx-2] mt-1">Owner: {d.owner}</div>
              </div>
            ))}
          </div>
        </Panel>

        <Panel title="What changed since last review?" testId="cc-what-changed-panel">
          <div className="space-y-2 max-h-[340px] overflow-y-auto thin-scroll pr-1">
            {cc.what_changed.map((w) => (
              <div key={w.id} className="flex items-start gap-2">
                <StatusChip tone={CHANGE_TONE[w.type] || "neutral"} className="shrink-0 mt-0.5 w-[74px] justify-center">{w.type}</StatusChip>
                <div>
                  <div className="text-[11px] text-[--tx-2] leading-snug">{w.item}</div>
                  <div className="text-[10px] font-mono text-[--tx-3] mt-0.5">{w.when}</div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        <Panel title="Confirmed drivers (evidence-backed)" testId="cc-confirmed-drivers">
          <div className="space-y-1.5">
            {cc.confirmed_drivers.map((f) => (
              <button key={f.id} onClick={() => navigate("/triad", { state: { findingId: f.id } })}
                className="w-full text-left rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2 hover:border-[--heat-3] transition-colors group" data-testid={`cc-driver-${f.id}`}>
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[10px] text-[--tx-3]">{f.id}</span>
                  <SeverityChip severity={f.severity} />
                  <ArrowUpRight size={11} className="ml-auto text-[--tx-3] group-hover:text-[--ai]" />
                </div>
                <div className="text-[11px] text-[--tx-2] leading-snug mt-1">{f.title}</div>
              </button>
            ))}
          </div>
        </Panel>

        <Panel title="Suspected drivers (hypotheses)" testId="cc-suspected-drivers">
          <div className="space-y-1.5">
            {cc.suspected_drivers.map((f) => (
              <button key={f.id} onClick={() => navigate("/triad", { state: { findingId: f.id } })}
                className="w-full text-left rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2 hover:border-[--heat-3] transition-colors group">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[10px] text-[--tx-3]">{f.id}</span>
                  <StatusChip tone="ai">Hypothesis</StatusChip>
                  <ArrowUpRight size={11} className="ml-auto text-[--tx-3] group-hover:text-[--ai]" />
                </div>
                <div className="text-[11px] text-[--tx-2] leading-snug mt-1">{f.title}</div>
              </button>
            ))}
          </div>
          <div className="mt-3 pt-2.5 border-t border-[--divider]">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1.5">Top unknowns</div>
            <ul className="space-y-1">
              {cc.top_unknowns.map((u, i) => <li key={i} className="text-[11px] text-[--tx-3] leading-snug pl-3 relative before:content-[''] before:absolute before:left-0 before:top-[6px] before:w-1 before:h-1 before:rounded-full before:bg-[--amber]">{u}</li>)}
            </ul>
          </div>
        </Panel>

        <Panel title="30-day pilot readiness" testId="cc-pilot-readiness">
          <div className="space-y-1.5">
            {cc.pilot_readiness.map((p) => (
              <div key={p.id} className="rounded-lg bg-[--shell-2] border border-[--divider] px-3 py-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="font-mono text-[10px] text-[--tx-3]">{p.id}</span>
                  <StatusChip tone={p.status.includes("ready") ? "validated" : "risk"} className="ml-auto">{p.status.includes("ready") ? "Ready" : "Gated"}</StatusChip>
                </div>
                <div className="text-[11px] text-[--tx-2] leading-snug">{p.name}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 pt-2.5 border-t border-[--divider] flex items-center justify-between text-[11px]">
            <span className="text-[--tx-3]">Action progress</span>
            <span className="text-[--tx-1] font-mono">{cc.action_progress.open} open / {cc.action_progress.total} total</span>
          </div>
        </Panel>

        <Panel title="Benefit realization tracker" testId="cc-benefit-tracker">
          <div className="space-y-2.5 text-xs">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-0.5">Status</div>
              <p className="text-[--tx-2] leading-relaxed">{cc.benefit_tracker.status}</p>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-0.5">Framework</div>
              <p className="text-[--tx-2] leading-relaxed">{cc.benefit_tracker.framework}</p>
            </div>
            <div className="rounded-lg border border-[--green] bg-[--green-soft] p-2.5">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[--green] mb-0.5">Current claim</div>
              <p className="text-[--tx-1] font-medium leading-snug">{cc.benefit_tracker.current_claim}</p>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[--tx-3] mb-1">Critical gaps in play</div>
              <div className="space-y-1">
                {cc.critical_gaps.map((g) => (
                  <div key={g.id} className="flex items-center gap-1.5 text-[11px] text-[--tx-2]">
                    <span className="font-mono text-[10px] text-[--tx-3]">{g.id}</span>
                    <span className="truncate">{g.title}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
}
